import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService, dashboardService } from '../services/auth';
import MerchantHeader from '../components/MerchantHeader';
import { CiScoreBadge } from '../components/CiScoreBadge';
import { AreaChart, Area, LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { TrendingUp, MousePointer, ShoppingCart, AlertTriangle, DollarSign, Package } from 'lucide-react';
import { toast } from 'sonner';

const fmt = (v, prefix = '') => (v === null || v === undefined ? 'No data measured' : prefix + Number(v).toLocaleString());

export default function PerformancePage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [products, setProducts] = useState([]);
  const [deals, setDeals] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showScenarioModal, setShowScenarioModal] = useState(false);
  const [showEditPriceModal, setShowEditPriceModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [newPromotionPrice, setNewPromotionPrice] = useState(0);
  const [priceHistory, setPriceHistory] = useState([]);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [configType, setConfigType] = useState(null);
  const [scarcityConfig, setScarcityConfig] = useState({
    // Low Stock Alert settings
    lowStockThreshold: 10,
    lowStockDiscount: 15,
    lowStockDuration: '48h', // 24h, 48h, 7d, until_sold_out
    showBadge: true,
    urgencyMessage: "Only {count} left in stock!",
    // Time-Limited Deal settings
    timeLimitedDiscount: 20,
    timeLimitedDuration: '24h', // 24h, 48h, 7d
    timeLimitedStartTime: 'now', // now, scheduled
    scheduledDate: '',
    showCountdown: true,
    dealEndMessage: "Deal ends in {time}!"
  });
  const [activeScenarios, setActiveScenarios] = useState([]);
  const [creatingScenario, setCreatingScenario] = useState(false);

  // REMOVED 2026-11-01 (honesty fix): hardcoded fake merchant metrics (12,450 clicks, $45,680 revenue ...)
  // were shown to every merchant as if measured. Restore ONLY behind an explicit demo flag, never as a
  // fallback for missing/failed data. Original kept below, commented:
  // // Mock performance data
  // const [performanceData] = useState({
  //   clicks: 12450,
  //   conversions: 892,
  //   abandonedCarts: 234,
  //   revenue: 45680,
  //   conversionRate: 7.2,
  //   avgOrderValue: 51.21,
  // });

  // Measured values from GET /api/merchant/analytics/deals/performance. null = not measured (never 0).
  const [performanceData, setPerformanceData] = useState({
    clicks: null, conversions: null, abandonedCarts: null, revenue: null, conversionRate: null, avgOrderValue: null,
  });
  const [perfError, setPerfError] = useState(null);

  // REMOVED 2026-11-01 (honesty fix): hardcoded fake merchant metrics (12,450 clicks, $45,680 revenue ...)
  // were shown to every merchant as if measured. Restore ONLY behind an explicit demo flag, never as a
  // fallback for missing/failed data. Original kept below, commented:
  // // Mock chart data
  // const [revenueChartData] = useState([
  //   { date: 'Jan 1', revenue: 1200, clicks: 450, conversions: 32 },
  //   { date: 'Jan 8', revenue: 1800, clicks: 680, conversions: 48 },
  //   { date: 'Jan 15', revenue: 2400, clicks: 890, conversions: 67 },
  //   { date: 'Jan 22', revenue: 2100, clicks: 780, conversions: 58 },
  //   { date: 'Jan 29', revenue: 3200, clicks: 1200, conversions: 89 },
  //   { date: 'Feb 5', revenue: 2800, clicks: 1050, conversions: 78 },
  //   { date: 'Feb 12', revenue: 3500, clicks: 1300, conversions: 95 },
  // ]);
  // No time-series endpoint is wired here yet: empty until measured.
  const [revenueChartData] = useState([]);

  // REMOVED 2026-11-01 (honesty fix): hardcoded 'Week 1-4' price/sales/ciScore series was shown as the
  // merchant's pricing impact. Restore ONLY behind an explicit demo flag. Original, commented:
  // const [pricingChartData] = useState([
  //   { date: 'Week 1', price: 49.99, sales: 120, ciScore: 680 },
  //   { date: 'Week 2', price: 44.99, sales: 180, ciScore: 750 },
  //   { date: 'Week 3', price: 39.99, sales: 250, ciScore: 820 },
  //   { date: 'Week 4', price: 42.99, sales: 210, ciScore: 790 },
  // ]);
  // No measured price-vs-sales series is wired yet: empty until measured.
  const [pricingChartData] = useState([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadPerformance = async () => {
    try {
      const res = await dashboardService.getDealsPerformance();
      const sm = (res && res.summary) || {};
      const pick = (k) => (sm[k] === undefined ? null : sm[k]);
      const clicks = pick('total_clicks');
      const conversions = pick('total_conversions');
      setPerformanceData({
        clicks, conversions, revenue: pick('total_revenue'), abandonedCarts: null, avgOrderValue: null,
        conversionRate: clicks && conversions !== null ? Math.round((conversions / clicks) * 1000) / 10 : null,
      });
      setPerfError(null);
    } catch (e) {
      // Honest error state: do NOT fall back to mock numbers.
      setPerfError('Could not load performance data. Nothing is shown as measured.');
    }
  };

  const loadData = async () => {
    try {
      const [profileData, productsData, dealsData] = await Promise.all([
        authService.getProfile(),
        dashboardService.getProducts(),
        dashboardService.getDeals()
      ]);
      setUser(profileData.member);
      setProducts(productsData.products || []);
      setDeals(dealsData.deals || []);
      setLoading(false);
      loadPerformance();
      
      // Also load scenarios
      await loadScenarios();
    } catch (error) {
      console.error('Failed to load data:', error);
      navigate('/login');
    }
  };

  const handleLogout = async () => {
    await authService.logout();
    sessionStorage.clear();
    navigate('/login');
  };

  const handleCreateScarcity = (product) => {
    setSelectedProduct(product);
    setShowScenarioModal(true);
  };

  const handleConfigureScenario = (type) => {
    setConfigType(type);
    setShowConfigModal(true);
  };

  const handleSaveScenarioConfig = async () => {
    setCreatingScenario(true);
    
    try {
      const token = sessionStorage.getItem('session_token');
      const API_URL = process.env.REACT_APP_BACKEND_URL || '';
      
      // Build request payload for backend
      const payload = {
        scenario_type: configType,
        auto_apply: true,
        max_products: 50
      };
      
      if (configType === 'low_stock') {
        payload.low_stock_config = {
          threshold: scarcityConfig.lowStockThreshold,
          discount: scarcityConfig.lowStockDiscount,
          duration: scarcityConfig.lowStockDuration,
          show_badge: scarcityConfig.showBadge,
          urgency_message: scarcityConfig.urgencyMessage
        };
      } else {
        payload.time_limited_config = {
          discount: scarcityConfig.timeLimitedDiscount,
          duration: scarcityConfig.timeLimitedDuration,
          start_time: scarcityConfig.timeLimitedStartTime,
          scheduled_date: scarcityConfig.scheduledDate || null,
          show_countdown: scarcityConfig.showCountdown,
          deal_end_message: scarcityConfig.dealEndMessage
        };
      }
      
      // Call backend API
      const response = await fetch(`${API_URL}/api/scenarios`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to create scenario');
      }
      
      const result = await response.json();
      
      // Add to active scenarios from backend response
      if (result.scenario) {
        setActiveScenarios(prev => [...prev, {
          id: result.scenario._id,
          type: result.scenario.scenario_type,
          config: result.scenario.config,
          status: 'active',
          dealsCreated: result.deals_created,
          createdAt: result.scenario.created_at
        }]);
      }
      
      setShowConfigModal(false);
      
      // Reload data to show new deals
      await loadData();
      
      toast.success(`${configType === 'low_stock' ? 'Low Stock Alert' : 'Time-Limited Deal'} activated!`, {
        description: result.message || `${result.deals_created} deals created`
      });
    } catch (error) {
      console.error('Failed to create scenario:', error);
      toast.error('Failed to activate scenario', {
        description: error.message || 'Please try again'
      });
    } finally {
      setCreatingScenario(false);
    }
  };

  // Load active scenarios from backend
  const loadScenarios = async () => {
    try {
      const token = sessionStorage.getItem('session_token');
      const API_URL = process.env.REACT_APP_BACKEND_URL || '';
      
      const response = await fetch(`${API_URL}/api/scenarios`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setActiveScenarios((data.scenarios || []).map(s => ({
          id: s._id,
          type: s.scenario_type,
          config: s.config,
          status: s.is_active ? 'active' : 'inactive',
          dealsCreated: s.deals_created || 0,
          createdAt: s.created_at
        })));
      }
    } catch (error) {
      console.error('Failed to load scenarios:', error);
    }
  };

  // Run scenario manually
  const handleRunScenario = async (scenarioId) => {
    try {
      const token = sessionStorage.getItem('session_token');
      const API_URL = process.env.REACT_APP_BACKEND_URL || '';
      
      const response = await fetch(`${API_URL}/api/scenarios/${scenarioId}/run`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) throw new Error('Failed to run scenario');
      
      const result = await response.json();
      
      toast.success('Scenario executed!', {
        description: `${result.deals_created} new deals created`
      });
      
      // Reload data
      await loadData();
      await loadScenarios();
    } catch (error) {
      toast.error('Failed to run scenario');
    }
  };

  // Deactivate scenario
  const handleDeactivateScenario = async (scenarioId) => {
    try {
      const token = sessionStorage.getItem('session_token');
      const API_URL = process.env.REACT_APP_BACKEND_URL || '';
      
      const response = await fetch(`${API_URL}/api/scenarios/${scenarioId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) throw new Error('Failed to deactivate scenario');
      
      setActiveScenarios(prev => prev.filter(s => s.id !== scenarioId));
      toast.success('Scenario deactivated');
    } catch (error) {
      toast.error('Failed to deactivate scenario');
    }
  };

  const openEditPriceModal = (product) => {
    setEditingProduct(product);
    
    // Find existing deal for this product
    const existingDeal = deals.find(d => d.product_id === product._id);
    
    // Calculate current deal price if deal exists
    const originalPrice = product.msrp || product.price || 0;
    let currentDealPrice = originalPrice;
    
    if (existingDeal) {
      currentDealPrice = existingDeal.discount_type === 'percentage'
        ? originalPrice * (1 - existingDeal.discount_value / 100)
        : originalPrice - existingDeal.discount_value;
    }
    
    setNewPromotionPrice(currentDealPrice);
    
    // Generate mock price history
    const history = [];
    for (let i = 30; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      const variance = (Math.random() - 0.5) * originalPrice * 0.1;
      history.push({
        date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        price: Math.max(currentDealPrice * 0.9, originalPrice * 0.7 + variance + (i * 0.5))
      });
    }
    history[history.length - 1].price = currentDealPrice;
    setPriceHistory(history);
    
    setShowEditPriceModal(true);
  };

  const handleUpdatePrice = async () => {
    if (!editingProduct) return;
    
    const originalPrice = editingProduct.msrp || editingProduct.price || 0;
    const discountValue = ((originalPrice - newPromotionPrice) / originalPrice) * 100;
    
    try {
      // Check if deal exists for this product
      const existingDeal = deals.find(d => d.product_id === editingProduct._id);
      
      if (existingDeal) {
        // Update existing deal
        await dashboardService.updateDeal(existingDeal._id, {
          discount_type: 'percentage',
          discount_value: Math.round(discountValue * 100) / 100
        });
      } else {
        // Create new deal
        await dashboardService.createDeal({
          product_id: editingProduct._id,
          discount_type: 'percentage',
          discount_value: Math.round(discountValue * 100) / 100,
          coupon_code: null,
          end_date: null
        });
      }
      
      setShowEditPriceModal(false);
      setEditingProduct(null);
      toast.success('Price updated successfully!', {
        description: `New promotion price: $${newPromotionPrice.toFixed(2)} (${discountValue.toFixed(0)}% off)`
      });
      loadData(); // Reload data
    } catch (error) {
      console.error('Failed to update price:', error);
      toast.error('Failed to update price', {
        description: error.response?.data?.detail || 'Please try again'
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{background: '#0B1120'}}>
        <div className="text-center">
          <svg className="w-12 h-12 text-[#3EF1C7] animate-spin mx-auto mb-4" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="text-[#9CB0C7]">Loading performance data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{background: '#0B1120'}}>
      <MerchantHeader user={user} onLogout={handleLogout} />

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">Performance Analytics</h1>
          <p className="text-[#9CB0C7]">Track clicks, conversions, and revenue across all your deals</p>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-8">
          {['overview', 'products', 'scenarios'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 rounded-lg font-medium capitalize transition-colors ${
                activeTab === tab
                  ? 'bg-[#7A5CFF] text-white'
                  : 'border border-[#22304A] bg-[#111A2E] text-[#9CB0C7] hover:bg-[#1A2437]'
              }`}
              data-testid={`tab-${tab}`}
            >
              {tab}
            </button>
          ))}
        </div>

        {activeTab === 'overview' && (
          <>
            {perfError && (<div role="alert" className="mb-4 rounded-lg border border-[#F55454] p-3 text-sm text-[#F55454]">{perfError}</div>)}
            {/* KPI Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <div className="rounded-lg border border-[#22304A] bg-[#0E1426]/80 backdrop-blur p-6">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm text-[#9CB0C7]">Total Clicks</p>
                  <MousePointer className="w-5 h-5 text-[#3EF1C7]" />
                </div>
                <p className="text-3xl font-bold text-white">{fmt(performanceData.clicks)}</p>
                <p className="text-xs text-[#9CB0C7] mt-1">{performanceData.clicks === null ? 'No clicks measured for this window' : 'Measured, last 30 days'}</p>
              </div>

              <div className="rounded-lg border border-[#22304A] bg-[#0E1426]/80 backdrop-blur p-6">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm text-[#9CB0C7]">Conversions</p>
                  <TrendingUp className="w-5 h-5 text-[#7A5CFF]" />
                </div>
                <p className="text-3xl font-bold text-white">{fmt(performanceData.conversions)}</p>
                <p className="text-xs text-[#8CF78E] mt-1">{performanceData.conversionRate === null ? 'Rate: no data measured' : performanceData.conversionRate + '% rate'}</p>
              </div>

              <div className="rounded-lg border border-[#22304A] bg-[#0E1426]/80 backdrop-blur p-6">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm text-[#9CB0C7]">Abandoned Carts</p>
                  <ShoppingCart className="w-5 h-5 text-[#FFC857]" />
                </div>
                <p className="text-3xl font-bold text-white">{fmt(performanceData.abandonedCarts)}</p>
                <p className="text-xs text-[#9CB0C7] mt-1">Abandoned carts are not tracked</p>
              </div>

              <div className="rounded-lg border border-[#22304A] bg-[#0E1426]/80 backdrop-blur p-6">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm text-[#9CB0C7]">Revenue</p>
                  <DollarSign className="w-5 h-5 text-[#8CF78E]" />
                </div>
                <p className="text-3xl font-bold text-[#8CF78E]">{fmt(performanceData.revenue, '$')}</p>
                <p className="text-xs text-[#9CB0C7] mt-1">{performanceData.avgOrderValue === null ? 'Avg order value: no data measured' : 'Avg: $' + performanceData.avgOrderValue}</p>
              </div>
            </div>

            {/* Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
              {/* Revenue Chart */}
              <div className="rounded-lg border border-[#22304A] bg-[#0E1426]/80 backdrop-blur p-6">
                <h3 className="text-lg font-semibold text-white mb-4">Revenue Over Time</h3>
                <p className="text-xs text-[#9CB0C7] mb-2">No time-series data measured</p>
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={revenueChartData}>
                    <defs>
                      <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#8CF78E" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#8CF78E" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#22304A" />
                    <XAxis dataKey="date" stroke="#9CB0C7" tick={{fill: '#9CB0C7', fontSize: 12}} />
                    <YAxis stroke="#9CB0C7" tick={{fill: '#9CB0C7', fontSize: 12}} />
                    <Tooltip contentStyle={{background: '#0E1426', border: '1px solid #22304A', borderRadius: '8px'}} />
                    <Area type="monotone" dataKey="revenue" stroke="#8CF78E" fillOpacity={1} fill="url(#colorRevenue)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>

              {/* Pricing Impact Chart */}
              <div className="rounded-lg border border-[#22304A] bg-[#0E1426]/80 backdrop-blur p-6">
                <h3 className="text-lg font-semibold text-white mb-4">Pricing Impact on Sales</h3>
                <p className="text-xs text-[#9CB0C7] mb-2">No pricing-impact data measured</p>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={pricingChartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#22304A" />
                    <XAxis dataKey="date" stroke="#9CB0C7" tick={{fill: '#9CB0C7', fontSize: 12}} />
                    <YAxis yAxisId="left" stroke="#9CB0C7" tick={{fill: '#9CB0C7', fontSize: 12}} />
                    <YAxis yAxisId="right" orientation="right" stroke="#9CB0C7" tick={{fill: '#9CB0C7', fontSize: 12}} />
                    <Tooltip contentStyle={{background: '#0E1426', border: '1px solid #22304A', borderRadius: '8px'}} />
                    <Legend />
                    <Line yAxisId="left" type="monotone" dataKey="price" stroke="#3EF1C7" name="Price ($)" />
                    <Line yAxisId="right" type="monotone" dataKey="sales" stroke="#7A5CFF" name="Sales" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Clicks & Conversions Chart */}
            <div className="rounded-lg border border-[#22304A] bg-[#0E1426]/80 backdrop-blur p-6">
              <h3 className="text-lg font-semibold text-white mb-4">Clicks vs Conversions</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={revenueChartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#22304A" />
                  <XAxis dataKey="date" stroke="#9CB0C7" tick={{fill: '#9CB0C7', fontSize: 12}} />
                  <YAxis stroke="#9CB0C7" tick={{fill: '#9CB0C7', fontSize: 12}} />
                  <Tooltip contentStyle={{background: '#0E1426', border: '1px solid #22304A', borderRadius: '8px'}} />
                  <Legend />
                  <Bar dataKey="clicks" fill="#3EF1C7" name="Clicks" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="conversions" fill="#7A5CFF" name="Conversions" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </>
        )}

        {activeTab === 'products' && (
          <div className="rounded-lg border border-[#22304A] bg-[#0E1426]/80 backdrop-blur overflow-hidden">
            <div className="p-6 border-b border-[#22304A]">
              <h3 className="text-lg font-semibold text-white">Product Performance</h3>
              <p className="text-sm text-[#9CB0C7]">View and manage pricing for each product</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#111A2E]">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#9CB0C7] uppercase">Product</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#9CB0C7] uppercase">Price</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#9CB0C7] uppercase">CI Score</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#9CB0C7] uppercase">Inventory</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#9CB0C7] uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#22304A]">
                  {products.map((product) => (
                    <tr key={product._id} className="hover:bg-[#111A2E]/40">
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <div className="h-10 w-10 flex-shrink-0 rounded-lg bg-[#1A2437] flex items-center justify-center">
                            <Package className="w-5 h-5 text-[#9CB0C7]" />
                          </div>
                          <div className="ml-4">
                            <p className="text-sm font-medium text-white">{product.title}</p>
                            <p className="text-xs text-[#9CB0C7]">{product.category}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-white font-medium">${product.price?.toFixed(2)}</td>
                      <td className="px-6 py-4"><CiScoreBadge score={product.ci_score} /></td>
                      <td className="px-6 py-4 text-[#9CB0C7]">{product.inventory || 0}</td>
                      <td className="px-6 py-4">
                        <div className="flex gap-2">
                          <button
                            onClick={() => openEditPriceModal(product)}
                            className="px-3 py-1 text-xs rounded bg-[#3EF1C7]/10 text-[#3EF1C7] hover:bg-[#3EF1C7]/20"
                            data-testid="edit-price-btn"
                          >
                            Edit Price
                          </button>
                          <button
                            onClick={() => handleCreateScarcity(product)}
                            className="px-3 py-1 text-xs rounded bg-[#7A5CFF]/10 text-[#7A5CFF] hover:bg-[#7A5CFF]/20"
                          >
                            Create Scarcity
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'scenarios' && (
          <div className="grid gap-6">
            {/* Active Scenarios */}
            {activeScenarios.length > 0 && (
              <div className="rounded-lg border border-[#3EF1C7]/30 bg-[#3EF1C7]/5 backdrop-blur p-6">
                <h3 className="text-lg font-semibold text-white mb-4">Active Scenarios</h3>
                <div className="grid gap-3">
                  {activeScenarios.map((scenario) => (
                    <div key={scenario.id} className="flex items-center justify-between p-4 bg-[#111A2E] rounded-lg border border-[#22304A]">
                      <div className="flex items-center gap-3">
                        <div className="w-2 h-2 rounded-full bg-[#8CF78E] animate-pulse"></div>
                        <div>
                          <p className="text-sm font-medium text-white">
                            {scenario.type === 'low_stock' ? 'Low Stock Alert' : 'Time-Limited Deal'}
                          </p>
                          <p className="text-xs text-[#9CB0C7]">
                            {scenario.type === 'low_stock' 
                              ? `≤${scenario.config?.threshold || 10} units → ${scenario.config?.discount || 15}% off (${scenario.config?.duration || '48h'})`
                              : `${scenario.config?.discount || 20}% off for ${scenario.config?.duration || '24h'}`}
                          </p>
                          <p className="text-xs text-[#3EF1C7] mt-1">
                            {scenario.dealsCreated || 0} deals created
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleRunScenario(scenario.id)}
                          className="px-3 py-1.5 text-xs rounded bg-[#3EF1C7]/10 text-[#3EF1C7] hover:bg-[#3EF1C7]/20 transition-colors"
                          data-testid={`run-scenario-${scenario.id}`}
                        >
                          Run Now
                        </button>
                        <button
                          onClick={() => handleDeactivateScenario(scenario.id)}
                          className="px-3 py-1.5 text-xs rounded bg-[#F55454]/10 text-[#F55454] hover:bg-[#F55454]/20 transition-colors"
                          data-testid={`deactivate-scenario-${scenario.id}`}
                        >
                          Deactivate
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="rounded-lg border border-[#22304A] bg-[#0E1426]/80 backdrop-blur p-6">
              <h3 className="text-lg font-semibold text-white mb-4">Scarcity Scenarios</h3>
              <p className="text-[#9CB0C7] mb-6">Create demand-based scarcity to increase urgency and conversions</p>
              
              <div className="grid gap-4">
                <div className="p-4 border border-[#22304A] rounded-lg bg-[#111A2E]">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-medium text-white">Low Stock Alert</h4>
                    <span className="px-2 py-1 text-xs rounded bg-[#FFC857]/10 text-[#FFC857]">High Impact</span>
                  </div>
                  <p className="text-sm text-[#9CB0C7] mb-3">Show "Only X left in stock" when inventory drops below threshold</p>
                  <button 
                    onClick={() => handleConfigureScenario('low_stock')}
                    className="px-4 py-2 rounded-lg bg-[#7A5CFF] text-white text-sm hover:bg-[#7A5CFF]/90"
                    data-testid="configure-low-stock-btn"
                  >
                    Configure
                  </button>
                </div>

                <div className="p-4 border border-[#22304A] rounded-lg bg-[#111A2E]">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-medium text-white">Time-Limited Deal</h4>
                    <span className="px-2 py-1 text-xs rounded bg-[#3EF1C7]/10 text-[#3EF1C7]">Medium Impact</span>
                  </div>
                  <p className="text-sm text-[#9CB0C7] mb-3">Create countdown timers for limited-time offers</p>
                  <button 
                    onClick={() => handleConfigureScenario('time_limited')}
                    className="px-4 py-2 rounded-lg bg-[#7A5CFF] text-white text-sm hover:bg-[#7A5CFF]/90"
                    data-testid="configure-time-limited-btn"
                  >
                    Configure
                  </button>
                </div>

                <div className="p-4 border border-[#22304A] rounded-lg bg-[#111A2E]">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-medium text-white">Demand Surge Pricing</h4>
                    <span className="px-2 py-1 text-xs rounded bg-[#7A5CFF]/10 text-[#7A5CFF]">AI-Powered</span>
                  </div>
                  <p className="text-sm text-[#9CB0C7] mb-3">Automatically adjust pricing based on real-time demand signals</p>
                  <button className="px-4 py-2 rounded-lg border border-[#22304A] text-[#9CB0C7] text-sm hover:bg-[#1A2437]">
                    Coming Soon
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Scarcity Scenario Modal */}
      {showScenarioModal && selectedProduct && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-[#0E1426] rounded-lg border border-[#22304A] max-w-md w-full p-6">
            <h2 className="text-xl font-bold text-white mb-4">Create Scarcity Scenario</h2>
            <p className="text-[#9CB0C7] mb-4">Product: {selectedProduct.title}</p>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#9CB0C7] mb-2">Scenario Type</label>
                <select className="w-full px-4 py-2 rounded-lg bg-[#111A2E] border border-[#22304A] text-white">
                  <option value="low_stock">Low Stock Alert</option>
                  <option value="time_limited">Time-Limited Deal</option>
                  <option value="flash_sale">Flash Sale</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-[#9CB0C7] mb-2">Threshold</label>
                <input
                  type="number"
                  defaultValue={10}
                  className="w-full px-4 py-2 rounded-lg bg-[#111A2E] border border-[#22304A] text-white"
                  placeholder="e.g., 10 units"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => setShowScenarioModal(false)}
                  className="flex-1 px-4 py-2 rounded-lg border border-[#22304A] text-white hover:bg-[#1A2437]"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    toast.success('Scarcity scenario created!');
                    setShowScenarioModal(false);
                  }}
                  className="flex-1 px-4 py-2 rounded-lg bg-[#7A5CFF] text-white hover:bg-[#7A5CFF]/90"
                >
                  Create
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Price Modal */}
      {showEditPriceModal && editingProduct && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-50" onClick={() => setShowEditPriceModal(false)}>
          <div className="bg-[#0E1426] rounded-lg border border-[#22304A] max-w-2xl w-full shadow-2xl" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-[#22304A]">
              <div>
                <h2 className="text-xl font-bold text-white">Edit Product Price</h2>
                <p className="text-sm text-[#9CB0C7] mt-1">{editingProduct.title}</p>
              </div>
              <button
                onClick={() => setShowEditPriceModal(false)}
                className="p-2 rounded-lg hover:bg-[#1E3A5F] transition-colors text-[#9CB0C7] hover:text-white"
              >
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-6">
              {/* Price Summary Cards */}
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="bg-[#111A2E] rounded-lg p-4 border border-[#22304A]">
                  <p className="text-xs text-[#9CB0C7] mb-1">Initial Price (MSRP)</p>
                  <p className="text-2xl font-bold text-white">
                    ${(editingProduct.msrp || editingProduct.price || 0).toFixed(2)}
                  </p>
                </div>
                <div className="bg-[#111A2E] rounded-lg p-4 border border-[#22304A]">
                  <p className="text-xs text-[#9CB0C7] mb-1">Current Deal Price</p>
                  <p className="text-2xl font-bold text-[#FFC857]">
                    ${(() => {
                      const existingDeal = deals.find(d => d.product_id === editingProduct._id);
                      if (!existingDeal) return (editingProduct.price || 0).toFixed(2);
                      const original = editingProduct.msrp || editingProduct.price || 0;
                      return existingDeal.discount_type === 'percentage'
                        ? (original * (1 - existingDeal.discount_value / 100)).toFixed(2)
                        : (original - existingDeal.discount_value).toFixed(2);
                    })()}
                  </p>
                </div>
                <div className="bg-[#3EF1C7]/10 rounded-lg p-4 border border-[#3EF1C7]/30">
                  <p className="text-xs text-[#3EF1C7] mb-1">New Promotion Price</p>
                  <p className="text-2xl font-bold text-[#3EF1C7]">${newPromotionPrice.toFixed(2)}</p>
                </div>
              </div>

              {/* Price Slider */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-[#9CB0C7] mb-3">Adjust Promotion Price</label>
                <div className="relative px-2">
                  <input
                    type="range"
                    min={0}
                    max={editingProduct.msrp || editingProduct.price || 100}
                    step={0.01}
                    value={newPromotionPrice}
                    onChange={(e) => setNewPromotionPrice(parseFloat(e.target.value))}
                    className="w-full h-2 bg-[#22304A] rounded-lg appearance-none cursor-pointer accent-[#3EF1C7]"
                    style={{
                      background: `linear-gradient(to right, #3EF1C7 0%, #3EF1C7 ${(newPromotionPrice / (editingProduct.msrp || editingProduct.price || 100)) * 100}%, #22304A ${(newPromotionPrice / (editingProduct.msrp || editingProduct.price || 100)) * 100}%, #22304A 100%)`
                    }}
                  />
                  <div className="flex justify-between mt-2 text-xs text-[#9CB0C7]">
                    <span>$0</span>
                    <span>${(editingProduct.msrp || editingProduct.price || 0).toFixed(2)}</span>
                  </div>
                </div>
              </div>

              {/* Manual Price Input */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-[#9CB0C7] mb-2">Or enter exact price</label>
                <div className="relative max-w-xs">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#9CB0C7]">$</span>
                  <input
                    type="number"
                    value={newPromotionPrice.toFixed(2)}
                    onChange={(e) => setNewPromotionPrice(parseFloat(e.target.value) || 0)}
                    step="0.01"
                    min="0"
                    max={editingProduct.msrp || editingProduct.price || 1000}
                    className="w-full pl-8 pr-4 py-3 rounded-lg border border-[#22304A] bg-[#111A2E] text-white text-lg font-bold focus:outline-none focus:border-[#3EF1C7] focus:ring-2 focus:ring-[#3EF1C7]/20"
                  />
                </div>
                <p className="text-xs text-[#9CB0C7] mt-2">
                  Discount: <span className="font-bold text-[#8CF78E]">
                    {((1 - newPromotionPrice / (editingProduct.msrp || editingProduct.price || 1)) * 100).toFixed(0)}% off
                  </span>
                </p>
              </div>

              {/* Price History Chart */}
              <div className="mb-6">
                <h3 className="text-sm font-medium text-[#9CB0C7] mb-3">Price History (30 Days)</h3>
                <div className="bg-[#111A2E] rounded-lg p-4 border border-[#22304A]">
                  <ResponsiveContainer width="100%" height={150}>
                    <AreaChart data={priceHistory}>
                      <defs>
                        <linearGradient id="priceGradientEdit" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#3EF1C7" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#3EF1C7" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#22304A" />
                      <XAxis 
                        dataKey="date" 
                        stroke="#9CB0C7" 
                        tick={{fill: '#9CB0C7', fontSize: 10}} 
                        tickLine={false}
                        interval="preserveStartEnd"
                      />
                      <YAxis 
                        stroke="#9CB0C7" 
                        tick={{fill: '#9CB0C7', fontSize: 10}} 
                        tickLine={false}
                        tickFormatter={(v) => `$${v.toFixed(0)}`}
                        domain={['dataMin - 5', 'dataMax + 5']}
                      />
                      <Tooltip 
                        contentStyle={{
                          background: '#0E1426', 
                          border: '1px solid #22304A', 
                          borderRadius: '8px',
                          color: '#fff'
                        }} 
                        formatter={(value) => [`$${value.toFixed(2)}`, 'Price']}
                      />
                      <Area 
                        type="monotone" 
                        dataKey="price" 
                        stroke="#3EF1C7" 
                        strokeWidth={2}
                        fillOpacity={1} 
                        fill="url(#priceGradientEdit)" 
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowEditPriceModal(false)}
                  className="flex-1 px-4 py-3 rounded-lg border border-[#22304A] bg-[#111A2E] text-white hover:bg-[#1A2437] transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleUpdatePrice}
                  className="flex-1 px-4 py-3 rounded-lg bg-[#3EF1C7] text-[#0B1120] font-bold hover:bg-[#3EF1C7]/90 transition-colors"
                  data-testid="update-price-btn"
                >
                  Update Price
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Scenario Configuration Modal */}
      {showConfigModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-50" onClick={() => setShowConfigModal(false)}>
          <div className="bg-[#0E1426] rounded-lg border border-[#22304A] max-w-lg w-full shadow-2xl" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-[#22304A]">
              <div>
                <h2 className="text-xl font-bold text-white">
                  {configType === 'low_stock' ? 'Configure Low Stock Alert' : 'Configure Time-Limited Deal'}
                </h2>
                <p className="text-sm text-[#9CB0C7] mt-1">
                  {configType === 'low_stock' 
                    ? 'Set up urgency messaging when inventory is low'
                    : 'Create countdown timers for your deals'}
                </p>
              </div>
              <button
                onClick={() => setShowConfigModal(false)}
                className="p-2 rounded-lg hover:bg-[#1E3A5F] transition-colors text-[#9CB0C7] hover:text-white"
              >
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
              {configType === 'low_stock' ? (
                <>
                  {/* Low Stock Threshold */}
                  <div>
                    <label className="block text-sm font-medium text-[#9CB0C7] mb-2">Stock Threshold</label>
                    <p className="text-xs text-[#9CB0C7] mb-3">Auto-create deal when inventory drops below this number</p>
                    <input
                      type="number"
                      value={scarcityConfig.lowStockThreshold}
                      onChange={(e) => setScarcityConfig({...scarcityConfig, lowStockThreshold: parseInt(e.target.value) || 0})}
                      min="1"
                      max="100"
                      className="w-full px-4 py-3 rounded-lg border border-[#22304A] bg-[#111A2E] text-white focus:outline-none focus:border-[#3EF1C7]"
                    />
                  </div>

                  {/* Discount Percentage */}
                  <div>
                    <label className="block text-sm font-medium text-[#9CB0C7] mb-2">Discount Percentage</label>
                    <p className="text-xs text-[#9CB0C7] mb-3">Discount to apply when deal is auto-created</p>
                    <div className="flex items-center gap-3">
                      <input
                        type="range"
                        min={5}
                        max={50}
                        value={scarcityConfig.lowStockDiscount}
                        onChange={(e) => setScarcityConfig({...scarcityConfig, lowStockDiscount: parseInt(e.target.value)})}
                        className="flex-1 h-2 bg-[#22304A] rounded-lg appearance-none cursor-pointer accent-[#3EF1C7]"
                      />
                      <span className="text-2xl font-bold text-[#3EF1C7] w-16 text-right">{scarcityConfig.lowStockDiscount}%</span>
                    </div>
                  </div>

                  {/* Deal Duration */}
                  <div>
                    <label className="block text-sm font-medium text-[#9CB0C7] mb-2">Deal Expires After</label>
                    <p className="text-xs text-[#9CB0C7] mb-3">How long should the deal last</p>
                    <div className="grid grid-cols-4 gap-2">
                      {[
                        { value: '24h', label: '24 Hours' },
                        { value: '48h', label: '48 Hours' },
                        { value: '7d', label: '7 Days' },
                        { value: 'until_sold_out', label: 'Until Sold Out' }
                      ].map((option) => (
                        <button
                          key={option.value}
                          onClick={() => setScarcityConfig({...scarcityConfig, lowStockDuration: option.value})}
                          className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                            scarcityConfig.lowStockDuration === option.value
                              ? 'bg-[#3EF1C7] text-[#0B1120]'
                              : 'bg-[#111A2E] border border-[#22304A] text-[#9CB0C7] hover:border-[#3EF1C7]'
                          }`}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Show Badge Toggle */}
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-white">Show Low Stock Badge</p>
                      <p className="text-xs text-[#9CB0C7]">Display urgency badge on product cards</p>
                    </div>
                    <button
                      onClick={() => setScarcityConfig({...scarcityConfig, showBadge: !scarcityConfig.showBadge})}
                      className={`w-12 h-6 rounded-full transition-colors ${scarcityConfig.showBadge ? 'bg-[#3EF1C7]' : 'bg-[#22304A]'}`}
                    >
                      <div className={`w-5 h-5 rounded-full bg-white transition-transform ${scarcityConfig.showBadge ? 'translate-x-6' : 'translate-x-0.5'}`}></div>
                    </button>
                  </div>

                  {/* Custom Message */}
                  <div>
                    <label className="block text-sm font-medium text-[#9CB0C7] mb-2">Urgency Message</label>
                    <input
                      type="text"
                      value={scarcityConfig.urgencyMessage}
                      onChange={(e) => setScarcityConfig({...scarcityConfig, urgencyMessage: e.target.value})}
                      className="w-full px-4 py-3 rounded-lg border border-[#22304A] bg-[#111A2E] text-white focus:outline-none focus:border-[#3EF1C7]"
                    />
                    <p className="text-xs text-[#FFC857] mt-2">Preview: {scarcityConfig.urgencyMessage.replace('{count}', scarcityConfig.lowStockThreshold)}</p>
                  </div>
                </>
              ) : (
                <>
                  {/* Discount Percentage */}
                  <div>
                    <label className="block text-sm font-medium text-[#9CB0C7] mb-2">Discount Percentage</label>
                    <p className="text-xs text-[#9CB0C7] mb-3">Discount to apply for the time-limited deal</p>
                    <div className="flex items-center gap-3">
                      <input
                        type="range"
                        min={5}
                        max={50}
                        value={scarcityConfig.timeLimitedDiscount}
                        onChange={(e) => setScarcityConfig({...scarcityConfig, timeLimitedDiscount: parseInt(e.target.value)})}
                        className="flex-1 h-2 bg-[#22304A] rounded-lg appearance-none cursor-pointer accent-[#7A5CFF]"
                      />
                      <span className="text-2xl font-bold text-[#7A5CFF] w-16 text-right">{scarcityConfig.timeLimitedDiscount}%</span>
                    </div>
                  </div>

                  {/* Start Time */}
                  <div>
                    <label className="block text-sm font-medium text-[#9CB0C7] mb-2">Start Time</label>
                    <div className="grid grid-cols-2 gap-2 mb-3">
                      <button
                        onClick={() => setScarcityConfig({...scarcityConfig, timeLimitedStartTime: 'now'})}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                          scarcityConfig.timeLimitedStartTime === 'now'
                            ? 'bg-[#7A5CFF] text-white'
                            : 'bg-[#111A2E] border border-[#22304A] text-[#9CB0C7] hover:border-[#7A5CFF]'
                        }`}
                      >
                        Start Now
                      </button>
                      <button
                        onClick={() => setScarcityConfig({...scarcityConfig, timeLimitedStartTime: 'scheduled'})}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                          scarcityConfig.timeLimitedStartTime === 'scheduled'
                            ? 'bg-[#7A5CFF] text-white'
                            : 'bg-[#111A2E] border border-[#22304A] text-[#9CB0C7] hover:border-[#7A5CFF]'
                        }`}
                      >
                        Schedule
                      </button>
                    </div>
                    {scarcityConfig.timeLimitedStartTime === 'scheduled' && (
                      <input
                        type="datetime-local"
                        value={scarcityConfig.scheduledDate}
                        onChange={(e) => setScarcityConfig({...scarcityConfig, scheduledDate: e.target.value})}
                        className="w-full px-4 py-3 rounded-lg border border-[#22304A] bg-[#111A2E] text-white focus:outline-none focus:border-[#7A5CFF]"
                      />
                    )}
                  </div>

                  {/* Deal Duration */}
                  <div>
                    <label className="block text-sm font-medium text-[#9CB0C7] mb-2">Deal Duration</label>
                    <p className="text-xs text-[#9CB0C7] mb-3">How long the deal should last</p>
                    <div className="grid grid-cols-3 gap-2">
                      {[
                        { value: '24h', label: '24 Hours' },
                        { value: '48h', label: '48 Hours' },
                        { value: '7d', label: '7 Days' }
                      ].map((option) => (
                        <button
                          key={option.value}
                          onClick={() => setScarcityConfig({...scarcityConfig, timeLimitedDuration: option.value})}
                          className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                            scarcityConfig.timeLimitedDuration === option.value
                              ? 'bg-[#7A5CFF] text-white'
                              : 'bg-[#111A2E] border border-[#22304A] text-[#9CB0C7] hover:border-[#7A5CFF]'
                          }`}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Show Countdown Toggle */}
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-white">Show Countdown Timer</p>
                      <p className="text-xs text-[#9CB0C7]">Display live countdown on deal cards</p>
                    </div>
                    <button
                      onClick={() => setScarcityConfig({...scarcityConfig, showCountdown: !scarcityConfig.showCountdown})}
                      className={`w-12 h-6 rounded-full transition-colors ${scarcityConfig.showCountdown ? 'bg-[#7A5CFF]' : 'bg-[#22304A]'}`}
                    >
                      <div className={`w-5 h-5 rounded-full bg-white transition-transform ${scarcityConfig.showCountdown ? 'translate-x-6' : 'translate-x-0.5'}`}></div>
                    </button>
                  </div>

                  {/* Custom Message */}
                  <div>
                    <label className="block text-sm font-medium text-[#9CB0C7] mb-2">End Message</label>
                    <input
                      type="text"
                      value={scarcityConfig.dealEndMessage}
                      onChange={(e) => setScarcityConfig({...scarcityConfig, dealEndMessage: e.target.value})}
                      className="w-full px-4 py-3 rounded-lg border border-[#22304A] bg-[#111A2E] text-white focus:outline-none focus:border-[#7A5CFF]"
                    />
                    <p className="text-xs text-[#FFC857] mt-2">Preview: {scarcityConfig.dealEndMessage.replace('{time}', scarcityConfig.timeLimitedDuration === '24h' ? '24h 00m' : scarcityConfig.timeLimitedDuration === '48h' ? '48h 00m' : '7d 00h')}</p>
                  </div>
                </>
              )}

              {/* Summary Section */}
              <div className="p-4 bg-[#111A2E] rounded-lg border border-[#22304A]">
                <p className="text-sm font-medium text-white mb-2">Summary</p>
                <div className="text-xs text-[#9CB0C7] space-y-1">
                  {configType === 'low_stock' ? (
                    <>
                      <p>• Applies to products with inventory ≤ {scarcityConfig.lowStockThreshold} units</p>
                      <p>• {scarcityConfig.lowStockDiscount}% discount will be applied</p>
                      <p>• Deal expires: {scarcityConfig.lowStockDuration === 'until_sold_out' ? 'When sold out' : `After ${scarcityConfig.lowStockDuration}`}</p>
                      <p className="text-[#FFC857] mt-2">Found {products.filter(p => (p.inventory || 0) <= scarcityConfig.lowStockThreshold && (p.inventory || 0) > 0 && !deals.some(d => d.product_id === p._id)).length} eligible products</p>
                    </>
                  ) : (
                    <>
                      <p>• {scarcityConfig.timeLimitedDiscount}% discount will be applied</p>
                      <p>• Starts: {scarcityConfig.timeLimitedStartTime === 'now' ? 'Immediately' : scarcityConfig.scheduledDate || 'Not set'}</p>
                      <p>• Duration: {scarcityConfig.timeLimitedDuration}</p>
                      <p className="text-[#FFC857] mt-2">Up to {Math.min(10, products.filter(p => !deals.some(d => d.product_id === p._id)).length)} products will get deals</p>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="p-6 border-t border-[#22304A] flex gap-3">
              <button
                onClick={() => setShowConfigModal(false)}
                disabled={creatingScenario}
                className="flex-1 px-4 py-3 rounded-lg border border-[#22304A] bg-[#111A2E] text-white hover:bg-[#1A2437] transition-colors font-medium disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveScenarioConfig}
                disabled={creatingScenario}
                className={`flex-1 px-4 py-3 rounded-lg font-bold transition-colors disabled:opacity-50 ${
                  configType === 'low_stock' 
                    ? 'bg-[#3EF1C7] text-[#0B1120] hover:bg-[#3EF1C7]/90'
                    : 'bg-[#7A5CFF] text-white hover:bg-[#7A5CFF]/90'
                }`}
                data-testid="activate-scenario-btn"
              >
                {creatingScenario ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Creating Deals...
                  </span>
                ) : (
                  'Activate & Create Deals'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
